import React, { useEffect, useState } from "react";
import { Download, Smartphone } from "lucide-react";
import { toast } from "sonner";

/**
 * PWA install button.
 *
 * The `beforeinstallprompt` event fires VERY early — often before React mounts.
 * We capture it globally in index.html and stash it on `window.__baimDeferredPwaPrompt`,
 * then read it here on mount and listen for the `baim-pwa-installable` custom event
 * for late-firing browsers.
 *
 * Three render modes:
 *   1. We have a deferred prompt → "Install app" button that triggers the native dialog.
 *   2. iOS Safari (no prompt API) → hint with Share → Add to Home Screen instructions.
 *   3. Android/desktop browsers that DON'T fire the event (already installed, criteria not met,
 *      or user previously dismissed) → "How to install" button that toasts a step-by-step
 *      so the user always has a path.
 *
 * Hidden when the app is already running standalone (already installed).
 */
export default function InstallPwaButton({ className = "", testid = "install-pwa-btn" }) {
  const [deferred, setDeferred] = useState(() => (typeof window !== "undefined" ? window.__baimDeferredPwaPrompt : null));
  const [isStandalone, setIsStandalone] = useState(false);
  const [platform, setPlatform] = useState("other"); // "ios" | "android" | "other"

  useEffect(() => {
    // Detect environment
    const ua = window.navigator.userAgent || "";
    const isIos = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    setPlatform(isIos ? "ios" : isAndroid ? "android" : "other");

    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true ||
      document.referrer.startsWith("android-app://");
    setIsStandalone(standalone);

    // Late capture (in case the event fires after mount)
    const onBeforeInstall = (e) => {
      e.preventDefault();
      window.__baimDeferredPwaPrompt = e;
      setDeferred(e);
    };
    const onInstallable = () => setDeferred(window.__baimDeferredPwaPrompt);
    const onInstalled = () => {
      window.__baimDeferredPwaPrompt = null;
      setDeferred(null);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("baim-pwa-installable", onInstallable);
    window.addEventListener("baim-pwa-installed", onInstalled);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("baim-pwa-installable", onInstallable);
      window.removeEventListener("baim-pwa-installed", onInstalled);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Already installed → don't show anything.
  if (isStandalone) return null;

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome !== "accepted") {
        toast.info("Install cancelled — you can try again from the More menu.");
      }
    } catch { /* user cancelled */ }
    window.__baimDeferredPwaPrompt = null;
    setDeferred(null);
  };

  // Mode 1: deferred prompt available — best UX.
  if (deferred) {
    return (
      <button
        type="button"
        onClick={install}
        data-testid={testid}
        className={`relative w-full flex items-center justify-center gap-2 px-3 h-10 bg-brand-orange text-white border border-brand-orange-600 text-sm rounded-md font-semibold hover:bg-brand-orange-600 ${className}`}
      >
        <Download size={14} /> Install app
      </button>
    );
  }

  // Mode 2: iOS Safari — manual instructions, persistent hint.
  if (platform === "ios") {
    return (
      <div
        data-testid={`${testid}-ios-hint`}
        className={`text-[11px] text-brand-navy-500 px-3 py-2 rounded-md bg-brand-cream-100 border border-brand-navy/15 leading-snug flex items-start gap-2 ${className}`}
      >
        <Smartphone size={14} className="shrink-0 mt-0.5" />
        <span>
          To install: tap the <span className="font-mono">Share</span> button, then{" "}
          <span className="font-mono">Add to Home Screen</span>.
        </span>
      </div>
    );
  }

  // Mode 3: Android/desktop without deferred prompt — "How to install" fallback.
  // Common reasons: user dismissed the prompt before, browser is Samsung Internet
  // with strict criteria, or Chrome cached the dismiss for 90 days.
  const howTo = () => {
    if (platform === "android") {
      toast.info(
        "Open the browser menu (⋮) and tap 'Install app' or 'Add to Home screen'. If you don't see it, refresh the page first.",
        { duration: 8000 }
      );
    } else {
      toast.info(
        "Click the install icon in the browser address bar, or open the browser menu and pick 'Install BAIM…'.",
        { duration: 8000 }
      );
    }
  };

  return (
    <button
      type="button"
      onClick={howTo}
      data-testid={`${testid}-howto`}
      className={`relative w-full flex items-center justify-center gap-2 px-3 h-10 bg-white border border-brand-navy/30 text-brand-navy text-sm rounded-md font-medium hover:bg-brand-cream-100 ${className}`}
    >
      <Download size={14} /> How to install the app
    </button>
  );
}
